
import React, { useEffect, useMemo, useState } from "react";

import {
  Box,
  Button,
  Chip,
  Divider,
  Paper,
  Stack,
  Typography,
} from "@mui/material";

import DownloadIcon from "@mui/icons-material/Download";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import PeopleIcon from "@mui/icons-material/People";
import RefreshIcon from "@mui/icons-material/Refresh";

import {
  DataGrid,
  GridToolbar,
} from "@mui/x-data-grid";

import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

import { procConsigneeList } from "../../../services/reportService";
import { generateReportPDF } from "../../../utils/pdfReport";

import BreadcrumbsBar from "../../../component/common/BreadcrumbsBar";

function ConsigneeList() {
  const [consigneeData, setConsigneeData] = useState([]);
  const [loading, setLoading] = useState(false);

  // =========================================================
  // LOAD DATA
  // =========================================================
  const rptConsigneeList = async () => {
    try {
      setLoading(true);

      const res = await procConsigneeList();

      setConsigneeData(res.data?.data || []);
    } catch (err) {
      console.error("Failed to load consignee list:", err);
      setConsigneeData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    rptConsigneeList();
  }, []);

  // =========================================================
  // CLEAN DATA FOR DISPLAY / EXPORT
  // =========================================================
  const cleanData = useMemo(() => {
    return consigneeData.map((item) => ({
      ShopCode: String(item.ShopCode ?? "").trim(),
      Consignee: String(item.Consignee ?? "").trim(),
      ConsigneeName: String(
        item.ConsigneeName ?? ""
      ).trim(),
    }));
  }, [consigneeData]);

  // =========================================================
  // EXCEL DOWNLOAD
  // =========================================================
  const downloadExcel = () => {
    if (!cleanData.length) {
      alert("No consignee records available.");
      return;
    }

    const data = cleanData.map((item) => ({
      "Shop Code": item.ShopCode,
      Consignee: item.Consignee,
      "Consignee Name": item.ConsigneeName,
    }));

    const worksheet =
      XLSX.utils.json_to_sheet(data);

    const workbook = XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(
      workbook,
      worksheet,
      "Consignee List"
    );

    // Improve Excel column widths
    worksheet["!cols"] = [
      { wch: 15 },
      { wch: 18 },
      { wch: 35 },
    ];

    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });

    const file = new Blob([excelBuffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    saveAs(
      file,
      "Consignee_List.xlsx"
    );
  };

  // =========================================================
  // PDF DOWNLOAD
  // =========================================================
  const downloadPDF = () => {
    if (!cleanData.length) {
      alert("No consignee records available.");
      return;
    }

    generateReportPDF({
      title: "Consignee List Report",

      fileName: "Consignee_List",

      columns: [
        "S.No",
        "Shop Code",
        "Consignee",
        "Consignee Name",
      ],

      rows: cleanData.map((item, index) => [
        index + 1,
        item.ShopCode,
        item.Consignee,
        item.ConsigneeName,
      ]),
    });
  };

  // =========================================================
  // DATAGRID ROWS
  // =========================================================
  const rows = useMemo(() => {
    return cleanData.map((item, index) => ({
      id: index + 1,
      serialNo: index + 1,
      ShopCode: item.ShopCode,
      Consignee: item.Consignee,
      ConsigneeName: item.ConsigneeName,
    }));
  }, [cleanData]);

  // =========================================================
  // DATAGRID COLUMNS
  // =========================================================
  const columns = [
    {
      field: "serialNo",
      headerName: "S.No",
      width: 80,
      headerAlign: "center",
      align: "center",
    },
    {
      field: "ShopCode",
      headerName: "Shop Code",
      width: 150,
    },
    {
      field: "Consignee",
      headerName: "Consignee",
      width: 170,
    },
    {
      field: "ConsigneeName",
      headerName: "Consignee Name",
      flex: 1,
      minWidth: 250,
    },
  ];

  return (
    <Box>
      {/* =====================================================
          BREADCRUMBS
      ===================================================== */}
      <BreadcrumbsBar page="Consignee List" />

      {/* =====================================================
          PROFESSIONAL HEADER
      ===================================================== */}
      <Paper
        elevation={0}
        sx={{
          mt: 2,
          mb: 3,
          p: {
            xs: 2.5,
            md: 3.5,
          },
          borderRadius: 3,
          border: "1px solid",
          borderColor: "divider",
          background:
            "linear-gradient(135deg, #f8fbff 0%, #ffffff 60%, #f4f8ff 100%)",
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* Decorative Circle */}
        <Box
          sx={{
            position: "absolute",
            right: -50,
            top: -60,
            width: 200,
            height: 200,
            borderRadius: "50%",
            background:
              "linear-gradient(135deg, rgba(25,118,210,0.12), rgba(25,118,210,0.02))",
          }}
        />

        <Stack
          direction={{
            xs: "column",
            sm: "row",
          }}
          spacing={2.5}
          alignItems={{
            xs: "flex-start",
            sm: "center",
          }}
          sx={{
            position: "relative",
            zIndex: 1,
          }}
        >
          {/* Icon */}
          <Box
            sx={{
              width: 64,
              height: 64,
              borderRadius: 2.5,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background:
                "linear-gradient(135deg, #1976d2, #42a5f5)",
              color: "white",
              boxShadow:
                "0 8px 20px rgba(25,118,210,0.25)",
              flexShrink: 0,
            }}
          >
            <PeopleIcon
              sx={{
                fontSize: 34,
              }}
            />
          </Box>

          {/* Title */}
          <Box sx={{ flex: 1 }}>
            <Typography
              variant="h4"
              fontWeight={700}
              sx={{
                fontSize: {
                  xs: "1.65rem",
                  md: "2rem",
                },
              }}
            >
              Consignee List
            </Typography>

            <Typography
              variant="body1"
              color="text.secondary"
              sx={{
                mt: 0.5,
                maxWidth: 650,
              }}
            >
              View, search and download the complete
              consignee master list.
            </Typography>
          </Box>

          {/* Record Count */}
          <Box
            sx={{
              minWidth: {
                xs: "100%",
                sm: 170,
              },
              textAlign: {
                xs: "left",
                sm: "right",
              },
            }}
          >
            <Typography
              variant="caption"
              color="text.secondary"
              fontWeight={600}
              sx={{
                textTransform: "uppercase",
                letterSpacing: 0.8,
              }}
            >
              Total Records
            </Typography>

            <Typography
              variant="h5"
              color="primary"
              fontWeight={800}
              sx={{
                mt: 0.2,
              }}
            >
              {cleanData.length.toLocaleString()}
            </Typography>

            <Chip
              label={
                cleanData.length
                  ? "Data Available"
                  : "No Records"
              }
              size="small"
              color={
                cleanData.length
                  ? "success"
                  : "default"
              }
              variant="outlined"
              sx={{
                mt: 0.5,
                fontWeight: 600,
              }}
            />
          </Box>
        </Stack>
      </Paper>

      {/* =====================================================
          REPORT ACTIONS
      ===================================================== */}
      <Paper
        elevation={0}
        className="no-print"
        sx={{
          mb: 3,
          borderRadius: 3,
          border: "1px solid",
          borderColor: "divider",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            px: {
              xs: 2,
              md: 3,
            },
            py: 2,
            backgroundColor: "grey.50",
            borderBottom: "1px solid",
            borderColor: "divider",
          }}
        >
          <Stack
            direction="row"
            spacing={1.5}
            alignItems="center"
          >
            <DownloadIcon
              color="primary"
              sx={{
                fontSize: 26,
              }}
            />

            <Box>
              <Typography
                variant="subtitle1"
                fontWeight={700}
              >
                Export Report
              </Typography>

              <Typography
                variant="body2"
                color="text.secondary"
              >
                Download the consignee list in Excel
                or PDF format.
              </Typography>
            </Box>
          </Stack>
        </Box>

        <Box
          sx={{
            p: {
              xs: 2,
              md: 2.5,
            },
          }}
        >
          <Stack
            direction={{
              xs: "column",
              sm: "row",
            }}
            spacing={1.5}
          >
            <Button
              variant="contained"
              startIcon={<DownloadIcon />}
              onClick={downloadExcel}
              disabled={
                loading ||
                cleanData.length === 0
              }
              sx={{
                minWidth: {
                  xs: "100%",
                  sm: 180,
                },
                height: 46,
                borderRadius: 2,
                fontWeight: 700,
                textTransform: "none",
              }}
            >
              Download Excel
            </Button>

            <Button
              variant="contained"
              color="error"
              startIcon={
                <PictureAsPdfIcon />
              }
              onClick={downloadPDF}
              disabled={
                loading ||
                cleanData.length === 0
              }
              sx={{
                minWidth: {
                  xs: "100%",
                  sm: 180,
                },
                height: 46,
                borderRadius: 2,
                fontWeight: 700,
                textTransform: "none",
              }}
            >
              Download PDF
            </Button>

            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={rptConsigneeList}
              disabled={loading}
              sx={{
                minWidth: {
                  xs: "100%",
                  sm: 130,
                },
                height: 46,
                borderRadius: 2,
                fontWeight: 600,
                textTransform: "none",
              }}
            >
              Refresh
            </Button>
          </Stack>
        </Box>
      </Paper>

      {/* =====================================================
          DATA TABLE
      ===================================================== */}
      <Paper
        elevation={0}
        sx={{
          width: "100%",
          borderRadius: 3,
          border: "1px solid",
          borderColor: "divider",
          overflow: "hidden",
        }}
      >
        {/* Table Header */}
        <Box
          sx={{
            px: {
              xs: 2,
              md: 3,
            },
            py: 2,
            backgroundColor: "grey.50",
          }}
        >
          <Stack
            direction={{
              xs: "column",
              sm: "row",
            }}
            spacing={1}
            alignItems={{
              xs: "flex-start",
              sm: "center",
            }}
            justifyContent="space-between"
          >
            <Box>
              <Typography
                variant="h6"
                fontWeight={700}
              >
                Consignee Records
              </Typography>

              <Typography
                variant="body2"
                color="text.secondary"
              >
                Search, filter and browse consignee
                information.
              </Typography>
            </Box>

            <Chip
              icon={<PeopleIcon />}
              label={`${cleanData.length.toLocaleString()} Records`}
              color="primary"
              variant="outlined"
              sx={{
                fontWeight: 600,
              }}
            />
          </Stack>
        </Box>

        <Divider />

        <Box
          sx={{
            height: {
              xs: 500,
              md: 620,
            },
            width: "100%",
          }}
        >
          <DataGrid
            rows={rows}
            columns={columns}
            loading={loading}
            initialState={{
              pagination: {
                paginationModel: {
                  page: 0,
                  pageSize: 10,
                },
              },
            }}
            pageSizeOptions={[
              10,
              25,
              50,
              100,
            ]}
            disableRowSelectionOnClick
            slots={{
              toolbar: GridToolbar,
            }}
            slotProps={{
              toolbar: {
                showQuickFilter: true,
                quickFilterProps: {
                  debounceMs: 500,
                },
              },
            }}
           sx={{
  border: 0,

  /* ================================
     COLUMN HEADER
     ================================ */
  "& .MuiDataGrid-columnHeaders": {
    backgroundColor: "#1976d2 !important",
    color: "#ffffff !important",
  },

  "& .MuiDataGrid-columnHeader": {
    backgroundColor: "#1976d2 !important",
    color: "#ffffff !important",
  },

  "& .MuiDataGrid-columnHeaderTitle": {
    color: "#ffffff !important",
    fontWeight: 700,
  },

 

  /* Menu / filter icon */
  "& .MuiDataGrid-columnHeader .MuiDataGrid-menuIconButton": {
    color: "#ffffff !important",
  },

  /* ================================
     TABLE CELLS
     ================================ */
  "& .MuiDataGrid-cell": {
    borderColor: "divider",
  },

  /* ================================
     ROW HOVER
     ================================ */
  "& .MuiDataGrid-row:hover": {
    backgroundColor: "rgba(30, 3, 236, 0.04)",
  },

  /* ================================
     TOOLBAR
     ================================ */
  "& .MuiDataGrid-toolbarContainer": {
    padding: "12px 16px",
    borderBottom: "1px solid",
    borderColor: "divider",
  },

  /* ================================
     FOOTER
     ================================ */
  "& .MuiDataGrid-footerContainer": {
    borderTop: "1px solid",
    borderColor: "divider",
  },
}}
          />
        </Box>
      </Paper>
    </Box>
  );
}

export default ConsigneeList;

